// Newsletter Admin API
import { api, generateCRUDInterface } from "aldoo-ra/API"
import config from "../../../config"

// Create single newsletter API endpoint
const newsletterAPI = api('newsletter')

// Create CRUD interface for newsletter subscribers
const crudAPI = api('crud')
const subscribersCRUD = generateCRUDInterface(crudAPI, 'Newsletter')

export const NewsletterAPI = {
  // Overview & Statistics
  async getOverview() {
    return await newsletterAPI({ 'get-overview': {} })
  },

  // Subscribers Management - Using CRUD API
  async getSubscribers(params = {}) {
    return await subscribersCRUD.Get(params)
  },

  async addSubscriber(subscriberData) {
    return await newsletterAPI({ 'add-subscriber': subscriberData })
  },

  async updateSubscriber(email, updateData) {
    return await newsletterAPI({ 
      'update-subscriber': {
        email,
        data: updateData
      }
    })
  },

  async deleteSubscriber(email, hardDelete = false) {
    return await newsletterAPI({ 
      'delete-subscriber': { 
        email,
        hardDelete 
      }
    })
  },

  async deleteSubscribers(subscriberEmails, hardDelete = true) {
    // Bulk delete by calling individual delete operations for each email
    const promises = subscriberEmails.map(async (email) => {
      return await newsletterAPI({ 
        'delete-subscriber': { 
          email,
          hardDelete 
        }
      })
    })
    
    const results = await Promise.all(promises)
    return { success: true, results }
  },

  async exportSubscribers(selectedFields = null) {
    // Use POST request through the normal API system to get file content
    const exportParams = {}
    
    // Add field selection if provided
    if (selectedFields) {
      exportParams.fields = selectedFields
    }
    
    const response = await newsletterAPI({ 'export-subscribers': exportParams })
    
    // The backend returns CSV content directly, so we need to handle it as a file
    if (response && typeof response === 'string') {
      // Create blob from CSV content
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' })
      return blob
    } else {
      throw new Error('Invalid export response format')
    }
  },

  async analyzeSubscribersImport(file) {
    const formData = new FormData()
    formData.append("analyze-subscribers-import", true)
    formData.append("file", file)

    return await newsletterAPI(formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  },

  async importSubscribers(file, importAsSubscribed = true) {
    const formData = new FormData()
    formData.append("import-subscribers", JSON.stringify({ importAsSubscribed }))
    formData.append("file", file)

    return await newsletterAPI(formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  },

  // Releases Management
  async getReleases(params = {}) {
    return await newsletterAPI({ 
      'get-releases': {
        page: 1,
        itemsPerPage: 25,
        sort: { createdAt: -1 },
        ...params
      }
    })
  },

  async createRelease(releaseData) {
    return await newsletterAPI({ 'create-release': releaseData })
  },

  async updateRelease(releaseId, updateData) {
    return await newsletterAPI({ 
      'update-release': {
        releaseId,
        ...updateData
      }
    })
  },

  async deleteRelease({ releaseId }) {
    return await newsletterAPI({ 'delete-release': { releaseId } })
  },

  async getRelease(releaseId) {
    return await newsletterAPI({ 
      'get-release': { 
        releaseId
      }
    })
  },

  async getReleaseDetails(releaseId) {
    return await newsletterAPI({ 
      'get-release-details': { 
        releaseId
      }
    })
  },

  // Release Control
  async scheduleRelease(releaseId, scheduledAt) {
    return await newsletterAPI({ 
      'schedule-release': { 
        releaseId, 
        scheduledAt 
      }
    })
  },

  async startRelease(releaseId) {
    return await newsletterAPI({ 'start-release': { releaseId } })
  },

  async pauseRelease(releaseId) {
    return await newsletterAPI({ 'pause-release': { releaseId } })
  },

  async resumeRelease(releaseId) {
    return await newsletterAPI({ 'resume-release': { releaseId } })
  },

  async cancelRelease(releaseId) {
    return await newsletterAPI({ 'cancel-release': { releaseId } })
  },

  // Templates Management
  async getTemplates() {
    return await newsletterAPI({ 'get-templates': {} })
  },

  async checkTemplateUsage(templateId) {
    return await newsletterAPI({
      'check-template-usage': {
        templateId
      }
    })
  },

  // Segments & Audience
  async getSegments() {
    return await newsletterAPI({ 'get-segments': {} })
  },

  async calculateAudience(segments, options = {}) {
    return await newsletterAPI({ 
      'calculate-audience': {
        segments,
        options
      }
    })
  },

  async previewAudience(segments = [], includeUnregistered = true, targetLanguages = [], targetPlatforms = [], ltvAmount, ltvOperator, deliveryOperator, deliveryReleases = [], audienceShare = 100) {
    return await newsletterAPI({
      'preview-audience': {
        segments,
        includeUnregistered,
        targetLanguages,
        targetPlatforms,
        ltvAmount,
        ltvOperator,
        deliveryOperator,
        deliveryReleases,
        audienceShare
      }
    })
  },

  // Settings
  async getSettings() {
    return await newsletterAPI({ 'get-settings': {} })
  },

  // Test Email
  async sendTestEmail(templateId, emails, testData = {}) {
    return await newsletterAPI({
      'send-test-email': {
        templateId,
        emails,
        testData
      }
    })
  },


  // Template Variables
  async getTemplateVariables(templateId) {
    return await newsletterAPI({
      'template-variables': {
        action: 'get-template-variables',
        templateId
      }
    })
  },

  async getTemplateVariables(templateId) {
    return await newsletterAPI({
      'template-variables': {
        action: 'get-template-variables',
        templateId
      }
    })
  },

  async getReleaseTemplateVariables(releaseId, templateId) {
    return await newsletterAPI({
      'template-variables': {
        action: 'get-release-template-variables',
        releaseId,
        templateId
      }
    })
  },

  async saveReleaseTemplateVariables(releaseId, templateId, localizationId) {
    return await newsletterAPI({
      'template-variables': {
        action: 'save-release-template-variables',
        releaseId,
        templateId,
        variables: localizationId
      }
    })
  },

  // Diagnostics & Testing
  async triggerScheduler() {
    return await newsletterAPI({ 'trigger-scheduler': {} })
  },

  async resetRelease(releaseId) {
    return await newsletterAPI({ 
      'reset-release': { 
        releaseId 
      }
    })
  }
}

export default NewsletterAPI